import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CustomButton from "@components/buttons/CustomButton";
import DataPill from "@components/pills/DataPill";
import { CurrencyLogoDropdown } from "@components/dropdowns/CurrencyLogoDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { profileServices } from "@services/apis/Profile";
import { transactionApiServices } from "@services/apis/Transactions";
import { GetCPGRatesResponseData } from "@services/models/Transactions/GetCPGRates";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import ConstantFunctions from "../../../utils/constants_functions";

const CPGRates = () => {
  const constantFunctions = new ConstantFunctions();
  const gridRef = useRef<AgGridReact>(null);
  const [rate, setRate] = useState<string>("");
  const [isReciprocal, setIsReciprocal] = useState<number>(0);
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [merchantId, setMerchantId] = useState<string>("");
  const [transactionType, setTransactionType] = useState<string>("payin");
  const [selectedFiat, setSelectedFiat] = useState<string | undefined>();
  const [selectedCrypto, setSelectedCrypto] = useState<string | undefined>();
  const { handleSuccess } = useSnackbarMessage();
  const [tokens, setTokens] = useState<any[]>([]);
  const [fiat, setFiat] = useState<any[]>([]);
  const [cpgRates, setCpgRates] = useState<GetCPGRatesResponseData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cpgMerchants, setCpgMerchants] = useState<any[]>([]);

  const reciprocalOptions = useMemo(
    () => [
      {
        id: 0,
        currency: "No",
        currencyLogo: "", // required by interface
        isToken: false,
      },
      {
        id: 1,
        currency: "Yes",
        currencyLogo: "",
        isToken: false,
      },
    ],
    [],
  );

  const handleSetRate = async () => {
    setBtnLoading(true);
    try {
      const requestBody = {
        merchantId,
        fromCurrencySymbol: selectedFiat,
        toCurrencySymbol: selectedCrypto,
        rate: parseFloat(rate),
        isReciprocal,
        transactionType,
      };

      await transactionApiServices.setExchangeRate(requestBody);

      // 🔁 RESET FORM
      setRate("");
      setIsReciprocal(0);
      setTransactionType("payin");

      // ✅ RESET DROPDOWNS TO DEFAULTS
      if (tokens.length > 0) {
        setSelectedCrypto(tokens[0].currencySymbol);
      }

      if (fiat.length > 0) {
        setSelectedFiat(fiat[0].currencySymbol);
      }

      handleSuccess({ message: "Exchange rate set successfully!" });
    } catch (error) {
      console.error(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const transactionTypeOptions = [
    {
      id: "payin",
      currency: "Payin",
      currencyLogo: "", // required by interface
    },
    {
      id: "payout",
      currency: "Payout",
      currencyLogo: "",
    },
  ];

  const fiatCurrencyOptions = useMemo(() => {
    return fiat.map((currency) => ({
      id: currency.currencySymbol, // ✅ SYMBOL, not ID
      currency: currency.currencySymbol,
      currencyLogo: currency.currencyImg,
      isToken: false,
    }));
  }, [fiat]);

  const cryptoCurrencyOptions = useMemo(() => {
    return tokens.map((token: any) => ({
      id: token.currencySymbol,
      currency: token.currencySymbol,
      currencyLogo: token.currencyImg, // ✅ REQUIRED by interface
      isToken: true,
    }));
  }, [tokens]);

  const cpgMerchantOptions = useMemo(() => {
    return cpgMerchants.map((merchant: any) => ({
      id: merchant.merchantId,
      currency:
        merchant.alias ?? merchant.categoryName ?? merchant.merchantName,
      currencyLogo: "",
      isToken: false,
    }));
  }, [cpgMerchants]);

  const showCpgMerchantDropdown = cpgMerchants.length > 1;

  const handleCpgMerchantChange = (value: string | number) => {
    const selectedId = String(value);
    setMerchantId(selectedId);
    getCPGRates(selectedId);
  };

  const getCPGRates = async (merchantId: string) => {
    try {
      setLoading(true);
      const response = await transactionApiServices.getCPGRates(
        String(merchantId),
      );
      setCpgRates(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching CPG Rates:", error);
    }
  };

  useEffect(() => {
    const getFiatCurrencies = async () => {
      try {
        const response =
          await transactionApiServices.getSupportedFiatCurrencies();

        const fiatList = response.data || [];
        setFiat(fiatList);

        // ✅ FIX: set symbol, not ID
        if (fiatList.length > 0) {
          setSelectedFiat(fiatList[0].currencySymbol);
        }
      } catch (error) {
        console.error("Error fetching currencies:", error);
      }
    };

    const getCryptoCurrencies = async () => {
      try {
        const response =
          await transactionApiServices.getSupportedCryptocurrencies();

        const cryptoList = response.data || [];
        setTokens(cryptoList);

        // ✅ set default crypto
        if (cryptoList.length > 0) {
          setSelectedCrypto(cryptoList[0].currencySymbol);
        }
      } catch (error) {
        console.error("Error fetching cryptocurrencies:", error);
      }
    };

    getCryptoCurrencies();
    getFiatCurrencies();
  }, []);

  useEffect(() => {
    const getProfileData = async () => {
      try {
        const response = await profileServices.getProfile();

        if (response?.data?.merchantDetails?.length) {
          const cpgMerchantsList = response.data.merchantDetails.filter(
            (merchant: any) => merchant.accountCategoryId === 6,
          );

          if (cpgMerchantsList.length) {
            setCpgMerchants(cpgMerchantsList);
            const defaultMerchant = cpgMerchantsList[0];
            setMerchantId(String(defaultMerchant.merchantId));
            getCPGRates(String(defaultMerchant.merchantId));
          }
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    };

    getProfileData();
  }, []);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Created At",
        field: "createdAt",
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
      },
      {
        headerName: "From",
        field: "fromCurrencySymbol",
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center justify-between h-full gap-1">
              <img
                src={data.fromCurrencyImage}
                alt={data.fromCurrencySymbol}
                height={14}
                width={14}
              />
              <span className="text-[10px] text-text-base font-medium truncate">
                {data.fromCurrencySymbol}
              </span>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "To",
        field: "toCurrencySymbol",
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center justify-between h-full gap-1">
              <img
                src={data.toCurrencyImage}
                alt={data.toCurrencySymbol}
                height={14}
                width={14}
              />
              <span className="text-[10px] text-text-base font-medium truncate">
                {data.toCurrencySymbol}
              </span>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Rate",
        field: "rate",
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <span className="text-[10px] text-text-base font-medium truncate">
              {data.rate}
            </span>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Is Reciprocal",
        field: "isReciprocal",
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <DataPill value={data?.isReciprocal === "1" ? "yes" : "no"} />
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Transaction Type",
        field: "transactionType",
        cellRenderer: (params: any) => {
          const { data } = params;
          const type = String(data?.transactionType ?? "").toLowerCase();
          const value =
            type === "payin" ? "pay-in" : type === "payout" ? "pay-out" : type;
          return <DataPill value={value} />;
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
    ],
    [constantFunctions],
  );

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px] w-full">
        <p className="flex text-text-base text-[14px] font-semibold items-center">
          CPG Rates
        </p>
      </div>
      <div
        className="flex flex-col gap-4 px-3 min-h-0 w-full overflow-hidden"
        style={{ height: "calc(100dvh - 70px)" }}
      >
        <div className="shrink-0 bg-bg-page-body rounded-lg w-full">
          <h2 className="text-text-base text-[14px] font-bold mt-4">
            Set Rate
          </h2>
          <div
            className={`grid ${showCpgMerchantDropdown ? "grid-cols-7" : "grid-cols-6"} w-[75%] gap-3 items-start`}
          >
            {showCpgMerchantDropdown && (
              <div className="flex flex-col gap-1">
                <CurrencyLogoDropdown
                  listOfValues={cpgMerchantOptions}
                  value={Number(merchantId)}
                  onChange={handleCpgMerchantChange}
                  label="Crypto Gateway"
                  color="bg-main-container-header"
                  baseColor="bg-bg-page-body"
                  borderColor="border-subtle"
                  expandedBorderColor="border-button-primary"
                  hideLogo
                />
              </div>
            )}
            <div className="">
              <GlobalTextInputField
                type="text"
                label="Exchange Rate"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="Rate"
                insideClassName="w-full h-[48px] py-[10px]"
              />
            </div>

            {/* Is Reciprocal */}
            <div className="flex flex-col gap-1">
              <CurrencyLogoDropdown
                listOfValues={reciprocalOptions}
                value={isReciprocal}
                onChange={(value) => setIsReciprocal(Number(value))}
                label="Is Reciprocal"
                color="bg-main-container-header"
                baseColor="bg-bg-page-body"
                borderColor="border-subtle"
                expandedBorderColor="border-button-primary"
                hideLogo
              />
            </div>

            {/* Transaction Type */}
            <div className="flex flex-col gap-1">
              <CurrencyLogoDropdown
                listOfValues={transactionTypeOptions}
                value={transactionType}
                onChange={(value) => setTransactionType(String(value))}
                label="Transaction Type"
                color="bg-main-container-header"
                baseColor="bg-bg-page-body"
                borderColor="border-subtle"
                expandedBorderColor="border-button-primary"
                hideLogo
              />
            </div>
            {/* From Currency */}

            <div className="flex flex-col gap-1">
              <CurrencyLogoDropdown
                listOfValues={fiatCurrencyOptions}
                value={selectedFiat}
                enableSearch
                searchableValues={fiatCurrencyOptions.map((f) => [f.currency])}
                onChange={(value) => setSelectedFiat(String(value))}
                label="From Currency"
                color="bg-main-container-header"
                baseColor="bg-bg-page-body"
                borderColor="border-subtle"
                expandedBorderColor="border-button-primary"
              />
          </div>

            {/* To Currency */}
            <div className="flex flex-col gap-1">
              <CurrencyLogoDropdown
                listOfValues={cryptoCurrencyOptions}
                value={selectedCrypto}
                enableSearch
                searchableValues={cryptoCurrencyOptions.map((c) => [
                  c.currency,
                ])}
                onChange={(value) => setSelectedCrypto(String(value))}
                label="To Currency"
                color="bg-main-container-header"
                baseColor="bg-bg-page-body"
                borderColor="border-subtle"
                expandedBorderColor="border-button-primary"
              />
            </div>

            {/* Button */}
            <div className="flex flex-col gap-1">
              {/* invisible label spacer */}
              <label className="text-xs font-medium opacity-0">Spacer</label>
              <CustomButton
                isActive={Boolean(rate && selectedFiat && selectedCrypto)}
                text="Set Rate"
                onClick={handleSetRate}
                className="w-full h-[48px]"
                isButtonLoading={btnLoading}
              />
            </div>
          </div>
        </div>
        <div className="flex-1 min-h-0 min-w-0 w-full">
          {loading && cpgRates.length === 0 ? (
            <AgGridSkeleton
              columnDefs={columnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={gridRef}
              rowData={cpgRates}
              columnDefs={columnDefs}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CPGRates;
